import React, { useState } from "react";
import { Sparkles, MapPin, ShieldCheck } from "lucide-react";
import axios from "axios";
import CityAutocomplete from "./CityAutocomplete";
import { DobField, TobField } from "./DobTimeFields";
import { useI18n } from "../contexts/I18nContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const label = { color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px", marginBottom: "7px" };
const hint = { color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", marginTop: "5px" };
const bigInput = { fontSize: "1rem", padding: "13px 14px" };

// Birth details, the friendly way: one tap for the date, one for the time.
// Native pickers beat three dropdowns on a phone by a mile.
export const InlineBirthForm = ({ onConnected, defaultName = "", title, subtitle }) => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: defaultName, dob: "", tob: "12:00", city: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.dob) { setError(lang === "hi" ? "कृपया जन्म तिथि चुनें।" : "Please pick your date of birth."); return; }
    const [year, month, day] = form.dob.split("-").map(Number);
    const [hour, minute] = (form.tob || "12:00").split(":").map(Number);
    setError(""); setRetrying(false); setLoading(true);
    const body = { name: form.name, year, month, day, hour, minute, city: form.city, lang };
    // The engine cold-starts and sometimes drops a request: retry quietly rather
    // than making the customer read an error and type everything again.
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await axios.post(`${API}/kundli/generate`, body, { withCredentials: true });
        const stored = {
          ...res.data,
          birth_details: { name: form.name, year, month, day, hour, minute, city: form.city },
        };
        localStorage.setItem("astrofate_report", JSON.stringify(stored));
        onConnected({ chart: res.data.chart || null, name: form.name, report_id: res.data.report_id || null, report_md: res.data.report || "" });
        setRetrying(false); setLoading(false);
        return;
      } catch (err) {
        const status = err.response?.status;
        const worthRetry = !status || status === 502 || status === 503 || status === 504;
        if (worthRetry && attempt < 3) {
          setRetrying(true);
          await new Promise(r => setTimeout(r, attempt * 3000));
          continue;
        }
        setError(err.response?.data?.detail || t("chart.genFailed"));
        break;
      }
    }
    setRetrying(false); setLoading(false);
  };

  return (
    <form onSubmit={submit} data-testid="inline-birth-form" className="glass-card birth-friendly"
      style={{ padding: "18px", border: "1px solid rgba(255,209,102,0.28)", background: "rgba(255,209,102,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
        <Sparkles size={16} style={{ color: "#FFD166" }} />
        <div className="font-cinzel" style={{ color: "#FFD166", fontSize: "1rem" }}>{title || t("chart.gateTitle")}</div>
      </div>
      <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", margin: "0 0 16px", lineHeight: 1.55 }}>
        {subtitle || t("chart.gateDesc")}
      </p>

      <div style={{ marginBottom: "14px" }}>
        <label style={label}>{t("birth.name")}</label>
        <input data-testid="inline-birth-name" type="text" value={form.name}
          onChange={set("name")} autoComplete="given-name"
          placeholder={t("birth.namePh")} className="cosmic-input" style={bigInput} required />
      </div>

      <DobField prefix="inline-birth" label={t("chart.dob")} hi={lang === "hi"}
        value={form.dob} onChange={(v) => setForm(f => ({ ...f, dob: v }))} />

      <TobField prefix="inline-birth" label={t("chart.tob")} hi={lang === "hi"}
        value={form.tob} onChange={(v) => setForm(f => ({ ...f, tob: v }))} />

      <div style={{ marginBottom: "16px" }}>
        <label style={label}><MapPin size={13} /> {t("birth.city")}</label>
        <CityAutocomplete value={form.city} onChange={(v) => setForm(f => ({ ...f, city: v }))} required name="city" />
        <p style={hint}>
          {lang === "hi" ? "शहर टाइप करें और सूची से चुनें।" : "Start typing your birth city and pick it from the list."}
        </p>
      </div>

      {error && (
        <div data-testid="inline-birth-error" style={{ color: "#fca5a5", fontSize: "0.82rem", marginBottom: "12px", padding: "9px 12px", background: "rgba(239,68,68,0.1)", borderRadius: "8px" }}>
          {error}
        </div>
      )}

      <button data-testid="inline-birth-submit" type="submit" disabled={loading} className="btn-gold"
        style={{ width: "100%", fontSize: "0.98rem", padding: "14px", opacity: loading ? 0.6 : 1 }}>
        {loading ? (retrying ? (lang === "hi" ? "फिर कोशिश कर रहे हैं…" : "Engine busy — retrying…") : t("chart.connecting")) : `✦ ${t("chart.connectBtn")} ✦`}
      </button>

      <p style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", color: "rgba(255,255,255,0.38)", fontSize: "0.72rem", marginTop: "11px" }}>
        <ShieldCheck size={12} style={{ color: "rgba(255,209,102,0.7)" }} />
        {lang === "hi" ? "20 सेकंड · निजी · कोई साइनअप नहीं" : "Takes 20 seconds · private · no signup needed"}
      </p>
    </form>
  );
};

export default InlineBirthForm;
